import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import accountApi from '../../account/api/accountApi';
import AccountRoles from '../../account/enums/AccountRoles';
import AutoComplete from '../../common/component/form/AutoComplete';
import ConfirmationDialog from '../../common/component/form/ConfirmationDialog';
import DateInput from '../../common/component/form/DateInput';
import Dropdown from '../../common/component/form/Dropdown';
import FileUpload from '../../common/component/form/FileUpload';
import RadioSelection from '../../common/component/form/RadioSelection';
import TextInput from '../../common/component/form/TextInput';
import {convertDateToIsoDateString} from '../../common/util/DateUtil';
import {command} from '../../common/util/DispatchUtil';
import slopeApi from '../../slope/api/slopeApi';
import teamApi from '../../team/api/teamApi';
import trainingApi from '../api/trainingApi';
import CourseSetting from '../enums/CourseSetting';
import Disciplines from '../enums/Disciplines';
import SlalomDistances, {SlalomDbToEnumString, SlalomDistanceDbString} from '../enums/SlalomDistances';
import SlopeConditions from '../enums/SlopeConditions';
import SnowTypes from '../enums/SnowTypes';
import TerrainTypes from '../enums/TerrainTypes';
import '../style/TrainingEdit.less';
import '../style/TrainingUpload.less';
import {emptyOrNumeric, isSlalomVariant, parseTraining} from '../util/TrainingUtilities';

function TrainingEdit(props) {
    const dispatch = useDispatch();
    const currentLanguage = useSelector(state => state.i18n.currentLanguage);
    const history = useHistory();
    const location = useLocation();
    const match = useRouteMatch();
    const role = useSelector(state => state.account.role);
    const account = useSelector(state => state.account);
    const [courseSetters, setCourseSetters] = useState([]);
    const [file, setFile] = useState('');
    const [date, setDate] = useState(new Date(Date.now()));
    const [training, setTraining] = useState();

    const [showTrainingDetails, setShowTrainingDetails] = useState(false);

    const disciplines = [Disciplines.SLALOM,
                         Disciplines.GIANT_SLALOM,
                         Disciplines.SUPER_G,
                         Disciplines.DOWNHILL];
    const [discipline, setDiscipline] = useState(null);

    const slalomDistances = [SlalomDistances.SLALOM_DISTANCE_4_6M,
                             SlalomDistances.SLALOM_DISTANCE_LESS_THAN_9M,
                             SlalomDistances.SLALOM_DISTANCE_9_11M,
                             SlalomDistances.SLALOM_DISTANCE_GREATER_11M];
    const giantSlalomDistances = [SlalomDistances.GIANT_SLALOM_DISTANCE_LESS_24M,
                                  SlalomDistances.GIANT_SLALOM_DISTANCE_24_26M,
                                  SlalomDistances.GIANT_SLALOM_DISTANCE_GREATER_26M];
    const [slalomDistance, setSlalomDistance] = useState([]);

    const snowTypes = Object.values(SnowTypes);
    const [snowType, setSnowType] = useState([]);

    const slopeConditions = Object.values(SlopeConditions);
    const [slopeCondition, setSlopeCondition] = useState([]);

    const [gateDistance, setGateDistance] = useState(null);
    const [numberOfGates, setNumberOfGates] = useState(null);
    const [turnarounds, setTurnarounds] = useState(null);

    const courseSettings = [CourseSetting.TURNING,
                            CourseSetting.SLIGHT_TURNING,
                            CourseSetting.STRAIGHT];
    const [courseSetting, setCourseSetting] = useState([]);

    const terrains = Object.values(TerrainTypes);
    const [terrain, setTerrain] = useState([]);

    const [courseSetter, setCourseSetter] = useState(null);
    const [locationOptions, setLocationOptions] = useState([]);

    const [selectedLocation, setSelectedLocation] = useState(null);
    const [allSlopes, setAllSlopes] = useState([]);
    const [slopeOptions, setSlopeOptions] = useState([]);
    const [selectedSlope, setSelectedSlope] = useState(null);
    const [canAddTraining, setCanAddTraining] = useState(true);

    const [showDeletionConfirmation, setShowDeletionConfirmation] = useState(false);
    const [deletionSelection, selectForDeletion] = useState();
    const [t] = useTranslation();

    const noEntryObj = {
        name: t('NO_ENTRY'),
        id: null
    };

    const [team, setTeam] = useState(noEntryObj);
    const [club, setClub] = useState();

    const [teamList, setTeamList] = useState([]);

    const clubList = useSelector(state => state.account.clubs);
    const [allSlopesLoaded, setAllSlopesLoaded] = useState(false);

    useEffect(() => {
        // component did mount
        loadTeamsOfClub();

        if (role !== AccountRoles.ADMINISTRATOR) {
            loadSlopes();
        }
        loadCourseSetters();

        if (match.params.id !== 'new') {
            trainingApi.getTrainingById(match.params.id).then(response => {
                if (response.data.success) {
                    const newTraining = parseTraining(response.data.result);
                    setTraining(newTraining);
                    setTrainingDetails(newTraining);
                }
            });
        } else {
            trainingApi.checkCanAddTraining().then(response => {
                if (response.data.success) {
                    setCanAddTraining(response.data.result);
                }
            });
        }
    }, [location]);

    useEffect(() => {
        setClub(training && training.club ? {
            id: training.club.id,
            name: training.club.name
        } : null);
    }, [training]);

    //dependency on club -> change team list options according to chosen club
    useEffect(() => {
        if (!club) {
            return;
        }

        loadTeamsOfClub(club?.id);

        if (club.id) {
            loadSlopes();
            loadCourseSetters();
        }

        setSelectedSlope(null);
        setSelectedLocation(null);
        setCourseSetter(null);

        if ((team.club?.id !== club.id) && (team.id !== null)) {
            handleDropDownTeamSelect(noEntryObj, noEntryObj);
        }
    }, [club]);

    useEffect(() => {
        const slopesOfLocation = allSlopes.filter((slopeVar) => slopeVar.location === selectedLocation && slopeVar.name);
        setSlopeOptions(slopesOfLocation);
    }, [allSlopes]);

    function setEmptySlope() {
        let emptySlopeOfLocation = allSlopes.find(allSlope => allSlope.location === selectedLocation && !allSlope.name);
        if (!emptySlopeOfLocation) {
            emptySlopeOfLocation = {location: selectedLocation};
        }
        setSelectedSlope(emptySlopeOfLocation);
    }

//dependency on location
    useEffect(() => {
        const slopesOfLocation = allSlopes.filter((slopeVar) => slopeVar.location === selectedLocation && slopeVar.name);
        setSlopeOptions(slopesOfLocation);
        if (!selectedLocation) {
            setSelectedSlope(null);
        } else if (selectedLocation && (!selectedSlope || !slopesOfLocation.some(slopeOfLocation => slopeOfLocation.id === selectedSlope.id) && allSlopesLoaded)) {
            setEmptySlope();
        }
    }, [selectedLocation]);

    function setTrainingDetails(newTraining) {
        setDate(newTraining.date ?? new Date(Date.now()));
        setCourseSetter(newTraining.courseSetter);
        setCourseSetting(newTraining.courseSetting ? newTraining.courseSetting.map(entry => {
            return {
                value: entry,
                label: t(`CourseSetting.${entry}`)
            };
        }) : []);
        setTurnarounds(newTraining.turnaround);
        setDiscipline(newTraining.discipline);
        if (isSlalomVariant(newTraining.discipline) && newTraining.gateDistance) {
            setSlalomDistance(newTraining.gateDistance ? newTraining.gateDistance.map(entry => {
                return {
                    value: SlalomDbToEnumString[entry],
                    label: t('SlalomDistances.' + entry)
                };
            }) : []);
        } else {
            setGateDistance(newTraining.gateDistance?.join(''));
        }
        setNumberOfGates(newTraining.gateCount);
        setSlopeCondition(newTraining.slopeCondition ? newTraining.slopeCondition.map(entry => {
            return {
                value: entry,
                label: t(`SlopeCondition.${entry}`)
            };
        }) : []);
        setSnowType(newTraining.snowType ? newTraining.snowType.map(entry => {
            return {
                value: entry,
                label: t(`SnowType.${entry}`)
            };
        }) : []);
        setTerrain(newTraining.terrain ? newTraining.terrain.map(entry => {
            return {
                value: entry,
                label: t(`Terrain.${entry}`)
            };
        }) : []);
        setSelectedSlope(newTraining?.slope);
        setSelectedLocation(newTraining?.slope?.location);
    }

    const handleCourseSetterChange = (setter) => {
        setCourseSetter(setter);
    };

    const handleLocationChange = (dest) => {
        setSelectedLocation(dest);
    };

    const handleSlopeChange = (slopeListItem) => {
        // User selected a already existing slope -> we get an object
        if (slopeListItem != null && typeof slopeListItem == 'object') {
            setSelectedSlope(slopeListItem);
        } else if (slopeListItem == null) {
            if (selectedLocation == null) {
                setSelectedSlope(null);
            } else {
                setEmptySlope();
            }
        }
        // User typed a name to create a new slope -> we get a string
        else {
            const slopeObject = {
                id: null,
                name: slopeListItem,
                location: selectedLocation
            };
            setSelectedSlope(slopeObject);
        }

    };

    function isValidSlalomDistance() {
        for (const selectedDistance of slalomDistance) {
            if (discipline === Disciplines.SLALOM) {
                if (!slalomDistances.some(distance => distance === selectedDistance.value)) {
                    return false;
                }
            } else {
                if (!giantSlalomDistances.some(distance => distance === selectedDistance.value)) {
                    return false;
                }
            }
        }
        return true;
    }

    const createDetails = () => {
        return {
            id: match.params.id === 'new' ? '00000000-0000-0000-0000-000000000000' : training.id,
            date: date ?? null,
            season: `${(new Date(Date.now()).getFullYear())}`,
            trainer: courseSetter,
            slope: selectedSlope,
            runCount: match.params.id === 'new' ? null : training.runs.length,
            turning: null,
            fluent: null,
            turnaround: turnarounds && typeof turnarounds === 'string' ? parseInt(turnarounds) : turnarounds,
            gateCount: numberOfGates && typeof numberOfGates === 'string' ? parseInt(numberOfGates) : numberOfGates,
            gateDistance: isSlalomVariant(discipline) && isValidSlalomDistance()
                ? slalomDistance?.map(distance => SlalomDistanceDbString[distance.value])
                : [gateDistance],
            snowType: snowType?.map(entry => entry.value),
            slopeCondition: slopeCondition?.map(entry => entry.value),
            terrain: terrain?.map(entry => entry.value),
            discipline,
            courseSetting: courseSetting?.map(entry => entry.value),
            courseSetter,
            teamId: team?.id,
            clubId: club ? club.id : null
        };
    };

    const sendTrainingDetails = (details) => {
        if (details) {
            trainingApi.setTrainingDetails(details).then(() => {
                redirectToReferer();
            });
        }
    };

    const uploadTraining = (details) => {
            const formData = new FormData();
            formData.append('file', file);

            for (const [key, value] of Object.entries(details)) {
                if (key === 'date' && value !== null) {
                    formData.append('date', convertDateToIsoDateString(value));
                } else if (value !== null && Array.isArray(value)) {
                    value.forEach(entry => {
                        if (entry !== null) {
                            formData.append(key, entry);
                        }
                    });
                } else if (value !== null && typeof value === 'object') {
                    formData.append(key + 'JSON', JSON.stringify(value));
                } else if (value !== null) {
                    formData.append(key, value);
                }
            }
            if (team.id) {
                formData.append('team-id', team.id);
            }
            trainingApi.uploadTraining(formData).then((response) => {
                if (response.data.success) {
                    redirectToReferer();
                } else {
                    setFile('');
                    setShowTrainingDetails(false);
                }
            });
        }
    ;

    String.prototype.capitalize = function () {
        return this.charAt(0).toUpperCase() + this.slice(1);
    };

    const handleFileSet = (files) => {
        setFile(files[0]);
    };

    const handleSave = () => {
        const details = createDetails();
        if (match.params.id === 'new') {
            uploadTraining(details);
        } else {
            sendTrainingDetails(details);
        }
    };

    const allowNextButton = () => {
        return date
            && (!!file || match.params.id !== 'new') // file exists OR we're not uploading, only changing details
            && discipline
            && (club && club.id || role !== AccountRoles.ADMINISTRATOR);
    };

    const validateInputs = () => {
        return !(!emptyOrNumeric(gateDistance)
            || !emptyOrNumeric(numberOfGates)
            || !emptyOrNumeric(turnarounds)
            || (selectedSlope && !selectedLocation));
    };

    const loadTeamsOfClub = (clubId) => {
        teamApi.getTeamsOfClub(clubId).then((response) => {
            if (response.data.success) {
                setTeamList([...response.data.result, noEntryObj]);
            }
        });
    };

    const handleDropDownTeamSelect = (key, value) => {
        let newTeam;
        if (value === noEntryObj) {
            newTeam = noEntryObj;
        } else {
            newTeam = {
                id: value.id,
                name: value.name,
                club: value.club,
                season: value.season
            };
        }

        if (newTeam.id) {
            setClub(newTeam.club);
        }
        setTeam(newTeam);
    };

    const handleDropDownClubSelect = (key, value) => {
        setClub({
            id: value.id,
            name: value.name
        });
    };

    const pickSlalomDistances = (chosenDiscipline) => {
        if (chosenDiscipline === Disciplines.SLALOM) {
            return slalomDistances.map((val) => {
                return {
                    value: val,
                    label: t('SlalomDistances.' + val)
                };
            });
        } else {
            return giantSlalomDistances.map((val) => {
                return {
                    value: val,
                    label: t('SlalomDistances.' + val)
                };
            });
        }
    };

    function loadCourseSetters() {
        let clubId;
        if (role === AccountRoles.ADMINISTRATOR) {
            clubId = club?.id;
        } else {
            clubId = account.club?.id;
        }
        if (clubId) {
            accountApi.findByClubId(clubId).then((response) => {
                if (response.data.success) {
                    setCourseSetters(response.data.result);
                }
            });
        }
        dispatch(command('SET_COURSE_SETTER', courseSetters));
    }

    function loadSlopes() {
        // Admins can set slopes of the current training club. Other users can set slopes of their club
        if (role === AccountRoles.ADMINISTRATOR) {
            slopeApi.findSlopesByClubId(club?.id).then((response) => {
                if (response.data.success) {
                    setAllSlopes(response.data.result);
                    //Map location names to list and filter out duplicates
                    setLocationOptions(response.data.result.map((slopeVar) => slopeVar.location).filter((value, index, self) => self.indexOf(value) === index));
                    //set this true so we don't overwrite training.slope with null at mount
                    setAllSlopesLoaded(true);
                }
            });
        } else {
            slopeApi.findAll().then((response) => {
                if (response.data.success) {
                    setAllSlopes(response.data.result);
                    //Map location names to list and filter out duplicates
                    setLocationOptions(response.data.result.map((slopeVar) => slopeVar.location).filter((value, index, self) => self.indexOf(value) === index));
                    //set this true so we don't overwrite training.slope with null at mount
                    setAllSlopesLoaded(true);
                }
            });
        }
    }

    function handleDeleteSlope(value) {
        setShowDeletionConfirmation(true);
        selectForDeletion({
            category: 'slope',
            value
        });
    }

    function handleDeleteLocation(value) {
        setShowDeletionConfirmation(true);
        selectForDeletion({
            category: 'location',
            value
        });
    }

    function deleteSlope(slope) {
        slopeApi.deleteSlopeById(slope.value.id).then((response) => {
            if (response.data.success) {
                loadSlopes();
                if (training && training.slope && training.slope.id !== slope.value.id && allSlopes.some(allSlope => allSlope.name === training.slope.name)) {
                    setSelectedSlope(training.slope);
                    setSelectedLocation(training.slope.location);
                } else {
                    setEmptySlope();
                }
            }
        }).catch((err) => {
            console.log('error deleting slope', err);
        });
    }

    function deleteLocation(slope) {
        slopeApi.deleteLocationByName(slope.value).then((response) => {
            if (response.data.success) {
                loadSlopes();
                if (training && training.slope && training.slope.location !== slope.value && allSlopes.some(allSlope => allSlope.id === training.slope.id)) {
                    setSelectedSlope(training.slope);
                    setSelectedLocation(training.slope.location);
                } else {
                    setSelectedSlope(null);
                    setSelectedLocation(null);
                }
            }
        }).catch((err) => {
            console.log('error deleting slope', err);
        });
    }

    function redirectToReferer() {
        match.params.id === 'new' ? history.push(`/trainings`) : history.push(`/training/${match.params.id}`);
    }

    function compareById(valueA, valueB) {
        if (!valueA || !valueB) {
            return false;
        }
        return valueA.id === valueB.id;
    }

    if (!canAddTraining) {
        return (
            <div className="training-edit">
                <h2>
                    {t('Training.ADD_TRAINING')}
                </h2>
                <div className="mt-3">{t('Training.FREE_TRAINING_LIMIT_REACHED')}</div>
                <div className="modal-footer">
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            redirectToReferer();
                        }}>
                        <FontAwesomeIcon icon="check" className="mr-2"/>
                        {t('Button.OK')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="training-edit">
            {
                !showTrainingDetails &&
                <> {/* required fields */}
                    <h2>
                        {match.params.id === 'new' ? t('Training.ADD_TRAINING') : t('Training.EDIT_TRAINING_DETAILS')}
                    </h2>
                    {
                        match.params.id === 'new' &&
                        <div className="form-container">
                            <FileUpload
                                isValid={true}
                                onFileDrop={handleFileSet}
                                onDelete={setFile}
                                showFilePreview={!!file}
                                allowedFileTypes={['.csv']}
                            />
                        </div>
                    }
                    <div className="form-container">
                        <h6>{t('Training.DATE')}*</h6>
                        <div>
                            <DateInput
                                selected={date}
                                onSelect={setDate}
                                label={t('Training.DATE')}
                                value={date}
                                onChange={setDate}
                                locale={currentLanguage}
                                maxDate={new Date(Date.now())}
                                timeFormat=""
                                timeIntervals={5}
                                timeCaption="time"
                                dateFormat="dd.MM.yyyy"
                                calendarClassName="react-datepicker--with-time"
                                required={true}
                            />
                        </div>
                    </div>
                    {role === AccountRoles.ADMINISTRATOR &&
                    <div>
                        <h6>{t('Training.CLUB')}*</h6>
                        <div>
                            <Dropdown
                                title={'Club'}
                                valueCompareFunction={compareById}
                                options={clubList ? Object.values(clubList).map((clubVar) => {
                                    return {
                                        label: clubVar ? clubVar.name : '',
                                        value: clubVar
                                    };
                                }) : []}
                                onChange={entry => handleDropDownClubSelect(entry.key, entry.value)}
                                value={{
                                    label: club ? `${club.name}` : '',
                                    value: club
                                }}>
                            </Dropdown>
                        </div>
                    </div>}
                    <div>
                        <h6>{t('Training.TEAM')}</h6>
                        <div>
                            <Dropdown
                                title={'Team'}
                                valueCompareFunction={compareById}
                                options={Object.values(teamList).map((clubTeam) => {
                                    return {
                                        label: clubTeam.name,
                                        value: clubTeam
                                    };
                                })}
                                onChange={entry => handleDropDownTeamSelect(entry.key, entry.value)}
                                value={{
                                    label: `${team.name}`,
                                    value: team
                                }}>
                            </Dropdown>
                        </div>
                    </div>
                    <div className="form-container">
                        <h6>{t('Training.DISCIPLINE')}*</h6>
                        <RadioSelection
                            options={disciplines?.map((val) => {
                                return {
                                    value: val,
                                    label: t('Disciplines.' + val)
                                };
                            })}
                            selected={discipline}
                            onChange={setDiscipline}
                            required={false}
                        />
                    </div>
                    <div className="modal-footer">
                        <button
                            className="btn btn-transparent-primary mr-3"
                            onClick={() => {
                                redirectToReferer();
                            }}>
                            <FontAwesomeIcon icon="times" className="mr-2"/>
                            {t('Button.CANCEL')}
                        </button>
                        <button
                            className={allowNextButton() ? 'btn btn-primary' : 'btn btn-primary disabled'}
                            onClick={() => {
                                if (allowNextButton()) {
                                    setShowTrainingDetails(true);
                                }
                            }
                            }>
                            {t('Button.NEXT')}
                            <FontAwesomeIcon icon="arrow-right" className="ml-2"/>
                        </button>
                    </div>
                </>
            }
            {
                showTrainingDetails &&
                <div>
                    <h3>
                        {t('Training.TRAINING_DETAILS')}
                        <span style={{color: '#C9C9C9'}}> - {t('Common.OPTIONAL')}</span>
                    </h3>
                    {
                        isSlalomVariant(discipline)
                            ?
                            <>
                                <div className="form-container">
                                    <h6>{t('Training.SLALOM_DISTANCE')}</h6>
                                    <Dropdown
                                        options={pickSlalomDistances(discipline)}
                                        value={slalomDistance}
                                        onChange={setSlalomDistance}
                                    />
                                </div>
                            </>
                            :
                            <>
                                <div className="form-container">
                                    <h6>{t('Training.GATE_DISTANCE')}</h6>
                                    <div className="row align-items-center">
                                        <TextInput
                                            id="gate distance"
                                            value={gateDistance}
                                            onChange={setGateDistance}
                                            required={false}
                                            showError={!emptyOrNumeric(gateDistance)}
                                            errorMessage={t('Error.NUMBERS_ONLY')}
                                        />
                                    </div>
                                </div>
                            </>
                    }
                    <div className="form-container">
                        <h6>{t('Training.SNOW_TYPE')}</h6>
                        <Dropdown
                            options={snowTypes?.map((val) => {
                                return {
                                    value: val,
                                    label: t('SnowTypes.' + val)
                                };
                            })}
                            value={snowType}
                            onChange={setSnowType}
                        />
                    </div>

                    <div className="form-container">
                        <h6>{t('Training.SLOPE_CONDITION')}</h6>
                        <Dropdown
                            options={slopeConditions?.map((val) => {
                                return {
                                    value: val,
                                    label: t('SlopeConditions.' + val)
                                };
                            })}
                            value={slopeCondition}
                            onChange={setSlopeCondition}
                        />
                    </div>
                    <div className="form-container">
                        <div className="inline-row mr-4">
                            <h6>{t('Training.NUMBER_OF_GATES')}</h6>
                            <TextInput
                                id="gate_number"
                                value={numberOfGates}
                                onChange={setNumberOfGates}
                                required={false}
                                showError={!emptyOrNumeric(numberOfGates)}
                                errorMessage={t('Error.NUMBERS_ONLY')}
                            />
                        </div>
                        <div className="inline-row">
                            <h6>{t('Training.CHANGES_OF_DIRECTION')}</h6>
                            <TextInput
                                id="direction_changes"
                                value={turnarounds}
                                onChange={setTurnarounds}
                                required={false}
                                showError={!emptyOrNumeric(turnarounds)}
                                errorMessage={t('Error.NUMBERS_ONLY')}
                            />
                        </div>
                    </div>

                    <div className="form-container">
                        <h6>{t('Training.COURSE_SETUP')}</h6>
                        <Dropdown
                            options={courseSettings?.map((val) => {
                                return {
                                    value: val,
                                    label: t('CourseSetting.' + val)
                                };
                            })}
                            value={courseSetting}
                            onChange={setCourseSetting}
                        />
                    </div>

                    <div className="form-container">
                        <h6>{t('Training.TERRAIN')}</h6>
                        <Dropdown
                            options={terrains?.map((val) => {
                                return {
                                    value: val,
                                    label: t('TerrainTypes.' + val)
                                };
                            })}
                            value={terrain}
                            onChange={setTerrain}
                        />
                    </div>

                    <div className="form-container">
                        <h6>{t('Training.COURSE_SETTER')}</h6>
                        <AutoComplete
                            options={courseSetters?.map((courseSetterAccount) => {
                                return {
                                    label: courseSetterAccount.firstName + ' ' + courseSetterAccount.lastName,
                                    value: courseSetterAccount.firstName + ' ' + courseSetterAccount.lastName
                                };
                            })}
                            setValue={handleCourseSetterChange}
                            value={courseSetter ? {
                                label: courseSetter,
                                value: courseSetter
                            } : null}
                        />
                    </div>
                    <div className="form-container">
                        <h6>{t('Training.LOCATION')}</h6>
                        <AutoComplete
                            options={locationOptions?.map((loc) => {
                                return {
                                    label: loc,
                                    value: loc
                                };
                            })}
                            setValue={handleLocationChange}
                            value={selectedLocation ? {
                                label: selectedLocation,
                                value: selectedLocation
                            } : null}
                            displayError={!selectedLocation && !!selectedSlope}
                            errorMessage={t('Error.LOCATION_REQUIRED')}
                            showDeleteIcon={true}
                            onDeleteOption={handleDeleteLocation}
                        />
                    </div>
                    <div className="form-container">
                        <h6>{t('Training.SLOPE')}</h6>
                        <AutoComplete
                            isDisabled={!selectedLocation}
                            options={slopeOptions?.map((slopeVar) => {
                                return {
                                    label: slopeVar?.name,
                                    value: slopeVar
                                };
                            })}
                            setValue={handleSlopeChange}
                            value={selectedSlope ? {
                                label: selectedSlope.name,
                                value: selectedSlope
                            } : null}
                            showDeleteIcon={true}
                            onDeleteOption={handleDeleteSlope}
                            placeholder={t('Training.SLOPE_PLACEHOLDER')}
                        />
                    </div>
                    <div className="modal-footer">
                        <button
                            className="btn btn-transparent-primary mr-3"
                            onClick={() => {
                                redirectToReferer();
                            }}>
                            <FontAwesomeIcon icon="times" className="mr-2"/>
                            {t('Button.CANCEL')}
                        </button>
                        <button
                            className={validateInputs() ? 'btn btn-primary' : 'btn btn-primary disabled'}
                            onClick={() => {
                                if (validateInputs()) {
                                    handleSave();
                                }
                            }}
                        >
                            <FontAwesomeIcon icon="check" className="mr-2"/>
                            {t('Button.SAVE')}
                        </button>
                    </div>
                    {
                        showDeletionConfirmation &&
                        <ConfirmationDialog
                            title={deletionSelection && deletionSelection.category === 'slope' ? t('Slope.DELETE_MODAL_TITLE_SLOPE') : t('Slope.DELETE_MODAL_TITLE_LOCATION')}
                            open={showDeletionConfirmation}
                            description={deletionSelection && deletionSelection.category === 'slope' ?
                                t('Slope.DELETE_MODAL_QUESTION_SLOPE', {slope: deletionSelection.value.name}) :
                                t('Slope.DELETE_MODAL_QUESTION_LOCATION', {location: deletionSelection.value})}
                            accept={() => {
                                deletionSelection.category === 'slope' ? deleteSlope(deletionSelection) : deleteLocation(deletionSelection);
                                selectForDeletion(undefined);
                                setShowDeletionConfirmation(false);

                            }}
                            cancel={() => {
                                selectForDeletion(undefined);
                                setShowDeletionConfirmation(false);
                            }}
                            acceptLabel={t('Button.DELETE')}
                        />
                    }
                </div>
            }
        </div>
    );
}

export default TrainingEdit;
